import { isNil, isNotNil, isString } from '@flowio/is';
import { PaymentButtons } from '../types';

export function resolveElement<T extends Element = HTMLElement>(
  elReference: T | string,
): T {
  if (isString(elReference)) {
    const node = document.querySelector<T>(elReference);
    if (isNil(node)) {
      throw new Error(`HTML element [${elReference}] not found in the document`);
    }
    return node;
  }
  if (!document.body.contains(elReference)) {
    throw new Error(`HTMLElement [${elReference.outerHTML}] not found in the document`);
  }
  return elReference;
}

export function resolveButton(
  buttons: PaymentButtons | undefined,
  key: keyof PaymentButtons,
): HTMLElement {
  const button = (isNotNil(buttons) && buttons[key]) || undefined;
  if (isNotNil(button)) {
    return resolveElement(button);
  }

  throw new Error(`Missing required payment button parameter: ${key}`);
}

export function removeElement(el: HTMLElement): void {
  if (isNotNil(el.parentNode)) {
    el.parentNode.removeChild(el);
  }
}

export function removeChildNodes(el: HTMLElement): HTMLElement {
  while (el.childNodes.length > 0) {
    el.childNodes[0].remove();
  }
  return el;
}

type CreatePaymentButtonParams = {
  text: string,
  classNames?: string[],
};

export function createPaymentButton(
  container: HTMLElement,
  params: CreatePaymentButtonParams,
): HTMLButtonElement {
  removeChildNodes(container);
  const button = document.createElement('button');
  button.innerHTML = params.text;
  const classes = (params.classNames || []).concat([
    'payment-button',
  ]);
  button.classList.add(...classes);
  container.appendChild(button);
  return button;
}

/**
 * @overview Helper method to return querySelectorAll results as an array to make it easier to work
 * with in IE :-(
 */
export function querySelectorAll<T extends HTMLElement = HTMLElement>(
  selector: string,
  container?: HTMLElement,
): T[] {
  const queryParent = container || document;
  const elements = queryParent.querySelectorAll(selector);
  return Array.prototype.slice.call(elements);
}

export function createElement<K extends keyof HTMLElementTagNameMap>(
  nodeName: K,
  attributes: Record<string, string>,
): HTMLElementTagNameMap[K] {
  const t = document.createElement(nodeName);
  Object.entries(attributes).forEach(([key, value]) => {
    t.setAttribute(key, value);
  });
  return t;
}
